import React, { useState } from 'react';
import { Wizard, WizardProps } from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { NameAndDescriptionEditor } from 'src/components/auditReports/createReportPage/nameAndDescriptionEditor';
import { AccessGrantConfigEditor } from 'src/components/auditReports/createReportPage/accessGrantConfigEditor';
import { ReviewAndSubmit } from 'src/components/auditReports/createReportPage/reviewAndSubmit';

export interface CreateReportWizardProps {
  name: string;
  setName: any;
  description: string;
  setDescription: any;
  outputFormats: string[];
  setOutputFormats: any;
  accessProvider: string;
  setAccessProvider: any;
  governanceTag: string;
  setGovernanceTag: any;
  resourceArn: string;
  setResourceArn: any;
  status: string;
  setStatus: any;
  startDate: string;
  setStartDate: any;
  endDate: string;
  setEndDate: any;
  handleCancel: any;
  handleSubmit: any;
  loading: boolean;
}

export const CreateReportWizard = (props: CreateReportWizardProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);

  const WIZARD_STEPS: WizardProps.Step[] = [
    {
      title: 'Name and description',
      info: <Link variant='info'>Info</Link>,
      description:
        'Provide a name and description for the report. This will only be used for your own reference. Then, select output format(s) for the report.',
      content: (
        <NameAndDescriptionEditor
          name={props.name}
          setName={props.setName}
          description={props.description}
          setDescription={props.setDescription}
          outputFormats={props.outputFormats}
          setOutputFormats={props.setOutputFormats}
        />
      ),
    },
    {
      title: 'Access grant config',
      isOptional: true,
      info: <Link variant='info'>Info</Link>,
      description: 'Set optional filters to configure what access grants will be included in the report.',
      content: (
        <AccessGrantConfigEditor
          {...props}
          accessProvider={props.accessProvider}
          setAccessProvider={props.setAccessProvider}
          governanceTag={props.governanceTag}
          setGovernanceTag={props.setGovernanceTag}
          resourceArn={props.resourceArn}
          setResourceArn={props.setResourceArn}
          status={props.status}
          setStatus={props.setStatus}
          startDate={props.startDate}
          setStartDate={props.setStartDate}
          endDate={props.endDate}
          setEndDate={props.setEndDate}
          displayAccessProviderFilter={false}
          displayDecisionDateRange={false}
        />
      ),
    },
    {
      title: 'Review and submit',
      info: <Link variant='info'>Info</Link>,
      description: 'Review the report configuration below, then submit to start report generation.',
      content: (
        <ReviewAndSubmit
          name={props.name}
          setName={props.setName}
          description={props.description}
          setDescription={props.setDescription}
          outputFormats={props.outputFormats}
          setOutputFormats={props.setOutputFormats}
          accessProvider={props.accessProvider}
          setAccessProvider={props.setAccessProvider}
          governanceTag={props.governanceTag}
          setGovernanceTag={props.setGovernanceTag}
          resourceArn={props.resourceArn}
          setResourceArn={props.setResourceArn}
          status={props.status}
          setStatus={props.setStatus}
          startDate={props.startDate}
          setStartDate={props.setStartDate}
          endDate={props.endDate}
          setEndDate={props.setEndDate}
        />
      ),
    },
  ];

  const WIZARD_I18N_STRINGS = {
    stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
    collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
    skipToButtonLabel: (step) => `Skip to ${step.title}`,
    navigationAriaLabel: 'Steps',
    cancelButton: 'Cancel',
    previousButton: 'Previous',
    nextButton: 'Next',
    submitButton: 'Submit',
    optional: 'optional',
  };

  return (
    <Wizard
      steps={WIZARD_STEPS}
      i18nStrings={WIZARD_I18N_STRINGS}
      activeStepIndex={activeStepIndex}
      onNavigate={({ detail }) => setActiveStepIndex(detail.requestedStepIndex)}
      onCancel={props.handleCancel}
      onSubmit={props.handleSubmit}
      isLoadingNextStep={props.loading}
      allowSkipTo={false}
    />
  );
};
